import { c as _c } from "react/compiler-runtime";
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MORPH_DURATION_MS, type MorphRect } from '../components/chat-widget/chat-widget-morph-layer';
import { takeChatWidgetMorphSuppressed } from './use-chat-widget-morphing';
const readRect = (el: HTMLElement | null): MorphRect | null => {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 || r.height === 0) return null;
  const radius = Number.parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0;
  return {
    top: r.top,
    left: r.left,
    width: r.width,
    height: r.height,
    radius
  };
};
type MorphState = {
  from: MorphRect;
  to: MorphRect;
  fromSig: string;
  toSig: string;
} | null;

// Overlay lingers (faded out) at the destination so the real box can fade in
// underneath — avoids a one-frame blink at handoff.
const SETTLE_MS = 120;

/**
 * FLIP-style morph between the chat's presentations (button / floating / docked /
 * fullscreen). On a `signature` change it animates a transient overlay from the
 * last painted rect to the new one, then cross-fades to the real box on arrival.
 *
 * @param signature   Changes whenever the active presentation changes.
 * @param getActiveEl Reads the currently-visible presentation's DOM node.
 * @param enabled     False skips every morph; presentations then swap in place.
 */
const useChatWidgetMorph = (signature, getActiveEl, t0) => {
  const $ = _c(23);
  const enabled = t0 === undefined ? true : t0;
  const prevSigRef = useRef(null);
  const paintedSigRef = useRef(null);
  const paintedRectRef = useRef(null);
  const [morph, setMorph] = useState(null);
  const [rect, setRect] = useState(null);
  const [phase, setPhase] = useState("animating");
  let t1;
  let t2;
  if ($[0] !== enabled || $[1] !== getActiveEl || $[2] !== signature) {
    t1 = () => {
      const sigChanged = prevSigRef.current !== null && prevSigRef.current !== signature;
      prevSigRef.current = signature;
      if (!sigChanged) {
        return;
      }
      const fromSig = paintedSigRef.current;
      const from = paintedRectRef.current;
      const to = readRect(getActiveEl());
      const suppressed = takeChatWidgetMorphSuppressed();
      if (enabled && !suppressed && fromSig !== null && fromSig !== signature && from && to && (from.width !== to.width || from.height !== to.height || from.top !== to.top || from.left !== to.left)) {
        setMorph({
          from,
          to,
          fromSig,
          toSig: signature
        });
        setRect(from);
        setPhase("animating");
      } else {
        setMorph(null);
        setRect(null);
        setPhase("animating");
      }
    };
    t2 = [signature, getActiveEl, enabled];
    $[0] = enabled;
    $[1] = getActiveEl;
    $[2] = signature;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useLayoutEffect(t1, t2);
  let t3;
  let t4;
  if ($[5] !== getActiveEl || $[6] !== signature) {
    t3 = () => {
      const id = requestAnimationFrame(() => {
        paintedSigRef.current = signature;
        paintedRectRef.current = readRect(getActiveEl());
      });
      return () => cancelAnimationFrame(id);
    };
    t4 = [signature, getActiveEl];
    $[5] = getActiveEl;
    $[6] = signature;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[9] !== morph) {
    t5 = () => {
      if (!morph) {
        return;
      }
      const id_0 = requestAnimationFrame(() => setRect(morph.to));
      return () => cancelAnimationFrame(id_0);
    };
    t6 = [morph];
    $[9] = morph;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useLayoutEffect(t5, t6);
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setPhase("settling");
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const onArrived = t7;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setMorph(null);
      setRect(null);
      setPhase("animating");
    };
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const onFaded = t8;
  let t10;
  let t9;
  if ($[14] !== morph) {
    t9 = () => {
      if (!morph) {
        return;
      }
      const id_1 = setTimeout(onFaded, MORPH_DURATION_MS + SETTLE_MS + 80);
      return () => clearTimeout(id_1);
    };
    t10 = [morph];
    $[14] = morph;
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  useEffect(t9, t10);
  const involvesButton = morph != null && (morph.fromSig === "button" || morph.toSig === "button");
  const t11 = morph != null;
  const t12 = morph != null && phase === "animating";
  const t13 = phase === "settling";
  let t14;
  if ($[17] !== involvesButton || $[18] !== rect || $[19] !== t11 || $[20] !== t12 || $[21] !== t13) {
    t14 = {
      isMorphing: t11,
      isAnimating: t12,
      settling: t13,
      settleMs: SETTLE_MS,
      rect,
      involvesButton,
      onArrived,
      onFaded
    };
    $[17] = involvesButton;
    $[18] = rect;
    $[19] = t11;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  return t14;
};
export default useChatWidgetMorph;